import { AuthContextProvider, ServiceEnvironment } from '@streamelements/client-sdk-js';
import { ScopedStorage } from './ScopedStorageService';
import { TwitchApiClient } from './Twitch';
import { AddOutputInfo, OutputId, ReadScopedStorageItemInfoResult, VideoCompositionId } from '../types/obs';
import { YoutubeApiClient } from './YouTube';
import { promisefy } from '../utils/promisefy';
import { AllowedProvider, platformPrettyName } from '../constants/common';
import { TiktokApiClient } from './Tiktok';
import { XApiClient } from './X';
import { ensureOBSPresence } from '../utils/ensureOBSPresence';
import { getPlatformAccessToken } from '../api/enableDestination';
import { AudioCompositionsService } from './AudioCompositionsService';
import { TrovoApiClient } from './Trovo';
import { KickApiClient } from './Kick';
import { createUserScopedStorageContainerItemInfo } from '../utils/scoped-storage';
import { loadVideoComposition } from '../routes/_authenticated.canvases/$canvasId/-videoCompositionCrudUtils';
import { createAndCaptureError } from '../utils/createAndCaptureError';
import { navigateInModal } from '../utils/navigateInModal';
import { FatalError, /*LockTimeoutError*/ } from '../types/errors';

export type ConnectOptionProps = {
  platform: 'twitch' | 'twitter' | 'youtube';
};

type BaseOutputAuxiliaryData = {
  destinationId: string;
  /**
   * A primary output is an output that is used to set the settings for the default canvas of OBS.
   * There can be only one primary output.
   * */
  isPrimaryOutput: boolean;
  streamTitle?: string;
  streamCategory?: string;
};

export type XOutputAuxiliaryData = BaseOutputAuxiliaryData & {
  broadcastId?: string;
  sourceId?: string;
  /**
   * 1 - No one
   * 2 - Everyone
   * 3 - Verified accounts
   * 4 - Accounts I follow
   * 5 - My subscribers
   */
  chatAccessOption: '1' | '2' | '3' | '4' | '5';
};

export type BaseOutputAuxiliaryDataWithBroadcastId = BaseOutputAuxiliaryData & {
  broadcastId?: string;
};

export type YouTubeOutputAuxiliaryData = BaseOutputAuxiliaryDataWithBroadcastId & {
  streamPrivacyStatus?: 'public' | 'unlisted' | 'private';
  broadcastId?: string;
  liveStreamId?: string;
};

export type TikTokOutputAuxiliaryData = BaseOutputAuxiliaryDataWithBroadcastId;

type ProviderAuxiliaryDataMap = {
  x: XOutputAuxiliaryData;
  youtube: YouTubeOutputAuxiliaryData;
  tiktok: TikTokOutputAuxiliaryData;
};

type OutputAuxiliaryData<T extends AllowedProvider> = T extends keyof ProviderAuxiliaryDataMap
  ? ProviderAuxiliaryDataMap[T]
  : BaseOutputAuxiliaryData;

export interface Destination<T extends AllowedProvider = AllowedProvider> {
  id: string;
  displayName: string;
  provider: T;
  providerId: string;
  accessToken: string;
  expiresAt: string;
  refreshToken: string;
  refreshExpiresAt: string;
  error?: Error;
  isEnabled?: boolean;
  isActive?: boolean;
  outputs: AddOutputInfo<OutputAuxiliaryData<T>>[];
}

async function getScopedStorageIdObject(authContextProvider: AuthContextProvider) {
  return createUserScopedStorageContainerItemInfo({
    authContextProvider,
    itemId: 'destinations',
  });
}

export const ALLOWED_OUTPUT_COUNT_PER_PROVIDER: Record<AllowedProvider, number> = {
  twitch: 1,
  youtube: 2,
  x: 1,
  tiktok: 1,
  custom: Infinity,
  trovo: 1,
  kick: 1,
};

type LockOptions = {
  lock: boolean;
};

async function unlocked<T>(marker: string, callback: () => Promise<T>): Promise<T> {
  void marker;

  let result: unknown = undefined;
  let error: unknown = undefined;

  try {
    result = await callback();
  } catch (e) {
    error = e;
  }

  if (error) {
    throw error;
  } else {
    return result as T;
  }
}

async function locked<T>(marker: string, callback: () => Promise<T>, options?: LockOptions): Promise<T> {
  const LOCK_KEY = 'DestinationsApiClient.global';

  const lock = options ? options.lock : true;

  if (!lock) {
    return await unlocked(marker, callback);
  } else {
    return (await navigator.locks.request(
        LOCK_KEY,
        async () => {
          let result: unknown = undefined;
          let error: unknown = undefined;

          try {
            result = await callback();
          } catch (e) {
            error = e;
          }

          if (error) {
            throw error;
          } else {
            return result;
          }
        },
    )) as T;
  }
}

export class DestinationsStorageApiClient {
  private readonly authContextProvider: AuthContextProvider;
  private readonly serviceEnvironment: ServiceEnvironment;

  constructor({
    authContextProvider,
    serviceEnvironment,
  }: {
    serviceEnvironment: ServiceEnvironment;
    authContextProvider: AuthContextProvider;
  }) {
    this.serviceEnvironment = serviceEnvironment;
    this.authContextProvider = authContextProvider;
  }

  public async initDestinations(): Promise<void> {
    await ensureOBSPresence();

    return await locked('initDestinations', async () => {
      const idObj = await getScopedStorageIdObject(this.authContextProvider);
      const scopedStorageItem = await ScopedStorage.readScopedStorageJsonItem<Destination[]>(idObj);
      const { content: destinations } = scopedStorageItem ?? { content: [] };

      let didFixOutputs = false;

      const fixedDestinations = destinations.map((destination) => {
        return {
          ...destination,
          outputs: destination.outputs.map((output) => {
            // Check that required fields exist
            if (output?.videoCompositionId && output?.audioCompositionId) return output;

            console.warn('Encountered an output that is null or does not have all required fields, fixing it', {
              destinationId: destination.id,
              destinationName: destination.displayName,
              destinationProvider: destination.provider,
            });

            // Sometimes our code does not create an output properly, causing the output to be null, so we need to re-create it.
            didFixOutputs = true;

            const fixed = DestinationMetadataApiClient.createDestinationOutputShape({
              destinationMetadata: destination,
              output: {
                name: destination.displayName,
                isEnabled: false,
                streamingSettings: {
                  serverUrl: '',
                  streamKey: '',
                },
              },
            });

            return fixed;
          })
        };
      });

      if (didFixOutputs) {
        await ScopedStorage.writeScopedStorageJsonItem({
          ...(await getScopedStorageIdObject(this.authContextProvider)),
          content: fixedDestinations,
        });

        window.location.reload();
      }
    });
  }

  public async getDestinations(lockOptions?: LockOptions): Promise<Destination[]> {
    await ensureOBSPresence();

    return await locked('getDestinations', async () => {
      try {
        const idObj = await getScopedStorageIdObject(this.authContextProvider);
        const scopedStorageItem = await ScopedStorage.readScopedStorageJsonItem<Destination[]>(idObj);
        const { content: destinations } = scopedStorageItem ?? { content: [] };

        const fixedDestinations = destinations.map((destination) => {
          return {
            ...destination,
            outputs: destination.outputs.filter((output) => {
              // Check that required fields exist
              return (!!output?.videoCompositionId && !!output?.audioCompositionId);
            }),
          };
        });

        return fixedDestinations;
      } catch {
        return [];
      }
    }, lockOptions) as Destination[];
  }

  public async getDestinationById({ id }: { id: string }, lockOptions?: LockOptions) {
    if (id === 'default') {
      return null;
    }

    const destinations = await this.getDestinations(lockOptions);
    return destinations.find((d) => d.id === id) || null;
  }

  public async removeDestinationById({ id }: { id: string }, lockOptions?: LockOptions): Promise<ReadScopedStorageItemInfoResult<Destination<AllowedProvider>[]>> {
    return await locked('removeDestinationById', async () => {
      const destinations = await this.getDestinations({ lock: false });

      const updatedStorage = await ScopedStorage.writeScopedStorageJsonItem({
        ...(await getScopedStorageIdObject(this.authContextProvider)),
        content: destinations.filter((d) => d.id !== id),
      });

      const outputs = await promisefy(window.host.getAllStreamingOutputs)();
      const outputsToRemove = (Object.values(outputs) as Destination['outputs'][number][]).filter(
        (output) => output.auxiliaryData?.destinationId === id
      );
      const audioCompositionsToRemove = outputsToRemove.filter(
        (output) => output.audioCompositionId && output.audioCompositionId !== 'default'
      );

      const ids = outputsToRemove.map((output) => output.id);
      const isRemovingPrimaryOutput = outputsToRemove.some((output) => output.auxiliaryData?.isPrimaryOutput);

      await promisefy(window.host.disableStreamingOutputsByIds)(ids.filter(i => i !== 'default'));
      await promisefy(window.host.removeStreamingOutputsByIds)(ids.filter(i => i !== 'default'));

      await Promise.all(
        audioCompositionsToRemove.map((output) => {
          return AudioCompositionsService.removeComposition({ audioCompositionId: output.audioCompositionId! });
        })
      );

      if (isRemovingPrimaryOutput) {
        await promisefy(window.host.setStreamingSettings)({
          serverUrl: '',
          streamKey: '',
        });
      }

      return updatedStorage;
    }, lockOptions);
  }

  public async getPlatformAccessToken(params: { provider: AllowedProvider; authContextProvider: AuthContextProvider }) {
    const {
      channel: { accessToken },
      token,
    } = await params.authContextProvider.getAuthContext();

    switch (params.provider) {
      case 'twitch':
        /** For twitch we need a special access token with specific scopes to be able to fetch a stream key. */
        return await TwitchApiClient.connectTwitch();
      case 'tiktok':
        throw new Error('Toktok is not supported by this client. Use TiktokApiClient.connectTiktok instead.');
      case 'x':
        return await Promise.resolve({ accessToken: token });
      default:
        return await Promise.resolve({ accessToken });
    }
  }

  public async storeDestination(
    { destination }: { destination: Destination }, options?: { notifyOBS?: boolean }, lockOptions?: LockOptions
  ): Promise<Destination<AllowedProvider>[]> {
    return await locked('storeDestination', async () => {
      const { notifyOBS = true } = options || ({} as never);
      const destinations = await this.getDestinations({ lock: false });
      const updated = new Map([...destinations].map((d) => [d.id, d]));

      updated.set(destination.id, destination);

      if (notifyOBS) {
        const destinationMetadataApiClient = new DestinationMetadataApiClient({
          destinationMetadata: destination,
          authContextProvider: this.authContextProvider,
          serviceEnvironment: this.serviceEnvironment,
        });

        await destinationMetadataApiClient.registerDestination(undefined, { lock: false });
      }

      const result = await ScopedStorage.writeScopedStorageJsonItem({
        ...(await getScopedStorageIdObject(this.authContextProvider)),
        content: Array.from(updated.values()),
      });

      return result.content;
    }, lockOptions) as Destination<AllowedProvider>[];
  }

  public async notifyOBSForActiveStreamingOutputs(params: { videoCompositionId?: VideoCompositionId } = {}, lockOptions?: LockOptions) {
    await locked('notifyOBSForActiveStreamingOutputs', async () => {
      const storedDestinations = await this.getDestinations({ lock: false });

      for (const destinationMetadata of storedDestinations) {
        if (destinationMetadata.provider === 'youtube') {
          Object.assign(window, {
            yt: new YoutubeApiClient({
              authContextProvider: this.authContextProvider,
              destinationMetadata: destinationMetadata as Destination<'youtube'>,
              serviceEnvironment: this.serviceEnvironment,
            }),
          });
        }

        try {
          await new DestinationMetadataApiClient({
            authContextProvider: this.authContextProvider,
            serviceEnvironment: this.serviceEnvironment,
            destinationMetadata,
          }).registerDestination(params, { lock: false });
        } catch(e) {
          console.warn('notifyOBSForActiveStreamingOutputs: failed registering destination with OBS: ', { error: e, destinationMetadata });
        }
      }
    }, lockOptions);
  }

  public async unmarkCurrentPrimaryOutput({
    notifyOBS = true,
  }: {
    notifyOBS?: boolean;
  } = {}, lockOptions?: LockOptions) {
    return await locked('unmarkCurrentPrimaryOutput', async () => {
      const currentPrimaryOutput = await DestinationsStorageApiClient.getPrimaryOutput();

      if (!currentPrimaryOutput?.auxiliaryData?.destinationId) {
        return;
      }

      const destination = await this.getDestinationById({ id: currentPrimaryOutput.auxiliaryData.destinationId }, { lock: false });

      if (!destination) {
        console.warn('Got a primary output without a destination object, removing it');
        await promisefy(window.host.removeStreamingOutputsByIds)([currentPrimaryOutput.id]);

        return;
      }

      const destinationMetadataApiClient = new DestinationMetadataApiClient({
        destinationMetadata: destination,
        authContextProvider: this.authContextProvider,
        serviceEnvironment: this.serviceEnvironment,
      });

      await destinationMetadataApiClient.updateDestination(
        {
          outputs: destination.outputs.map((output) => ({
            ...output,
            auxiliaryData: { ...output.auxiliaryData!, isPrimaryOutput: false },
          })),
        },
        {
          notifyOBS,
        },
        { lock: false }
      );
    }, lockOptions);
  }

  public async makePrimaryOutput({ outputId }: { outputId: OutputId }, lockOptions?: LockOptions) {
    return await locked('makePrimaryOutput', async () => {
      await this.unmarkCurrentPrimaryOutput({}, { lock: false });

      const outputs = (await promisefy(window.host.getAllStreamingOutputs)()) as Record<
        OutputId,
        Destination['outputs'][number]
      >;

      if (!outputs[outputId].auxiliaryData?.destinationId) {
        throw new Error('Output is not associated with a destination');
      }

      const destinationMetadata = await this.getDestinationById({ id: outputs[outputId].auxiliaryData.destinationId }, { lock: false });

      if (!destinationMetadata) {
        throw new Error('Destination metadata not found');
      }

      const newPrimaryDestinationMetadataApiClient = new DestinationMetadataApiClient({
        destinationMetadata,
        authContextProvider: this.authContextProvider,
        serviceEnvironment: this.serviceEnvironment,
      });

      const output = outputs[outputId];
      output.auxiliaryData.isPrimaryOutput = true;

      await newPrimaryDestinationMetadataApiClient.updateDestination(
        {
          outputs: destinationMetadata.outputs.map((o) => {
            if (o.id === outputId) {
              return output;
            }

            o.auxiliaryData.isPrimaryOutput = false;
            o.isEnabled = false;
            return o;
          }),
        },
        {
          notifyOBS: false,
        },
        { lock: false }
      );

      await promisefy(window.host.setStreamingSettings)({
        ...outputs[outputId].streamingSettings,
      });

      await newPrimaryDestinationMetadataApiClient.registerDestination(undefined, { lock: false });
    }, lockOptions);
  }

  public static async getPrimaryOutput() {
    const outputs = (await promisefy(window.host.getAllStreamingOutputs)()) as Record<
      OutputId,
      Destination['outputs'][number]
    >;

    return Object.values(outputs).find((output) => output.auxiliaryData?.isPrimaryOutput);
  }

  public async dispose() {
    void 0;
  }
}

export class DestinationMetadataApiClient {
  private readonly authContextProvider: AuthContextProvider;
  private readonly serviceEnvironment: ServiceEnvironment;
  private destinationMetadata: Destination;

  constructor({
    destinationMetadata,
    authContextProvider,
    serviceEnvironment,
  }: {
    destinationMetadata: Destination;
    authContextProvider: AuthContextProvider;
    serviceEnvironment: ServiceEnvironment;
  }) {
    this.authContextProvider = authContextProvider;
    this.destinationMetadata = destinationMetadata;
    this.serviceEnvironment = serviceEnvironment;
  }

  public get() {
    return this.destinationMetadata;
  }

  public isConnected() {
    //destinationMetadata.outputs.every((output) => output.auxiliaryData.destinationId === destinationMetadata.id)
    return this.destinationMetadata.outputs.some(
      (output) => (output.isEnabled || output.auxiliaryData?.isPrimaryOutput) && output.auxiliaryData?.destinationId === this.destinationMetadata.id
    );
  }

  public static createDestinationOutputIdTemplate({ destinationMetadata }: { destinationMetadata: Destination }) {
    return `${destinationMetadata.id}-${destinationMetadata.provider}-`;
  }

  public get destinationOutputIdTemplate() {
    return DestinationMetadataApiClient.createDestinationOutputIdTemplate({
      destinationMetadata: this.destinationMetadata,
    });
  }

  public async refreshTokens(lockOptions?: LockOptions) {
    let result: {
      accessToken: string;
      refreshToken: string;
      expiresAt: string;
      refreshExpiresAt: string;
    };

    switch (this.destinationMetadata.provider) {
      case 'tiktok':
        result = await TiktokApiClient.ensureFreshTokens(this.destinationMetadata);
        break;
      default: {
        const { accessToken } = await getPlatformAccessToken({
          authContextProvider: this.authContextProvider,
          serviceEnvironment: this.serviceEnvironment,
          channel: {
            _id: this.destinationMetadata.id,
          },
        });

        result = {
          accessToken,
          refreshToken: '',
          expiresAt: new Date(2050, 1, 1).toISOString(),
          refreshExpiresAt: new Date(2050, 1, 1).toISOString(),
        };
      }
    }

    await this.updateDestination(result, {}, lockOptions);

    return this.get();
  }

  private async storeMetadataToScopedStorage(content: Destination[], lockOptions?: LockOptions) {
    return await locked('storeMetadataToScopedStorage', async () => {
      const storageIdObject = await getScopedStorageIdObject(this.authContextProvider);

      const storageData = {
        ...storageIdObject,
        content,
      };

      await ScopedStorage.writeScopedStorageJsonItem(storageData);
    }, lockOptions);
  }

  public async updateDestination(
    destinationMetadata: Partial<Destination>,
    options: {
      notifyOBS?: boolean;
    } = { notifyOBS: true },
    lockOptions?: LockOptions,
  ) {
    return await locked('updateDestination', async () => {
      const { notifyOBS = true } = options || ({} as never);

      const destinationsApiClient = new DestinationsStorageApiClient({
        authContextProvider: this.authContextProvider,
        serviceEnvironment: this.serviceEnvironment,
      });

      if (destinationMetadata.id && destinationMetadata.id !== this.destinationMetadata.id) {
        throw new Error('Destination ID cannot be changed');
      }

      const destinations = await destinationsApiClient.getDestinations({ lock: false });

      let index = -1;

      const updated = destinations.map((d, i) => {
        if (d.id !== this.destinationMetadata.id) return d;
        index = i;
        return {
          ...d,
          ...this.destinationMetadata,
          ...destinationMetadata,
        };
      });

      if (index === -1) {
        updated.push({
          ...this.destinationMetadata,
          ...destinationMetadata,
        });
      }

      await this.storeMetadataToScopedStorage(updated, { lock: false });

      const next = updated[index];

      this.destinationMetadata = {
        ...this.destinationMetadata,
        ...next,
      };

      if (!notifyOBS) {
        return;
      }

      await this.registerDestination(undefined, { lock: false });
    }, lockOptions);
  }

  public static createDestinationOutputShape({
    destinationMetadata,
    output,
  }: {
    destinationMetadata: Destination;
    output?: Partial<Omit<Destination['outputs'][number], 'id'>>;
  }) {
    const [lastOutput] = destinationMetadata.outputs.filter((o) => !!o).slice(-1);
    const { id: lastOutputId } = lastOutput || {};
    const [destinationId, provider, outputId] = lastOutputId?.split('-') || [];

    void destinationId;
    void provider;

    const id = Number(outputId ?? 0) + 1;

    const outputIdTemplate = DestinationMetadataApiClient.createDestinationOutputIdTemplate({
      destinationMetadata,
    });

    const newOutput = {
      ...output,
      id: `${outputIdTemplate}${id}`,
      auxiliaryData: {
        ...(output?.auxiliaryData ?? {}),
        destinationId: destinationMetadata.id,
        isPrimaryOutput: output?.auxiliaryData?.isPrimaryOutput ?? false,
      },
    } as Destination['outputs'][number];

    if (!newOutput.videoCompositionId) {
      newOutput.videoCompositionId = 'default';
    }

    if (!newOutput.audioCompositionId) {
      newOutput.audioCompositionId = 'default';
    }

    return newOutput;
  }

  public async addOutput(
    output: Partial<Omit<Destination['outputs'][number], 'id'>>,
    options?: { notifyOBS?: boolean },
    lockOptions?: LockOptions,
  ) {
    return await locked('addOutput', async () => {
      try {
        const destinationOutputShape = DestinationMetadataApiClient.createDestinationOutputShape({
          destinationMetadata: this.destinationMetadata,
          output,
        });

        this.destinationMetadata.outputs.push(destinationOutputShape);

        if (options?.notifyOBS) {
          await this.registerDestination(undefined, { lock: false });
        }

        return destinationOutputShape;
      } catch (error) {
        console.warn('Failed to add output', {
          error,
          output: JSON.stringify(output),
          destinationMetadata: JSON.stringify(this.destinationMetadata),
        });
        throw error;
      }
    }, lockOptions);
  }

  /**
   * @param {object} props - An object containing configuration options.
   * @param {'default' | string} props.videoCompositionId - The video composition ID to register the destination for. If provided, only outputs for that video composition will be registered.
   */
  public async registerDestination(params: { videoCompositionId?: VideoCompositionId } = {}, lockOptions?: LockOptions) {
    return await unlocked('registerDestination', async () => {
      let stoppedOutputIds: string[] = [];
      console.info('Registering destination', this.destinationMetadata.displayName);

      await ensureOBSPresence();

      const videoCompositions = await promisefy(window.host.getAllVideoCompositions)();
      const audioCompositions = await promisefy(window.host.getAllAudioCompositions)();

      if (params.videoCompositionId && !videoCompositions[params.videoCompositionId]) {
        console.info('Loading video composition for destination', {
          videoCompositionId: params.videoCompositionId,
          destination: this.destinationMetadata,
        });

        const videoComposition = await loadVideoComposition({ videoCompositionId: params.videoCompositionId });

        videoCompositions[params.videoCompositionId] = videoComposition;
      }

      try {
        const deregisterResult = await this.deregisterDestination(params, lockOptions);

        stoppedOutputIds = deregisterResult.stoppedOutputIds;
      } catch {
        void 0;
      }

      for (const output of this.destinationMetadata.outputs) {
        if (!output?.videoCompositionId) {
          // All outputs must have a `videoCompositionId` field
          console.info('Outputs array malformed', {
            destinationMetadata: JSON.stringify({
              displayName: this.destinationMetadata.displayName,
              provider: this.destinationMetadata.provider,
            }),
            outputs: JSON.stringify(this.destinationMetadata.outputs),
          });
          continue;
        }

        const { videoCompositionId: outputVideoCompositionId = 'default' } = output;

        const shouldSkipOutputRegistration =
          params.videoCompositionId && outputVideoCompositionId !== params.videoCompositionId;

        if (shouldSkipOutputRegistration) {
          console.info(
            'Skipping output',
            output.name,
            `registration, because output.videoCompositionId (${outputVideoCompositionId}) is different than the params.videoCompositionId (${params.videoCompositionId})`
          );
          continue;
        }

        if (output.audioCompositionId && output.audioCompositionId !== 'default') {
          const audioComposition = await AudioCompositionsService.loadAudioComposition({
            audioCompositionId: output.audioCompositionId,
          });

          audioCompositions[output.audioCompositionId] = audioComposition;
        }

        if (output.videoCompositionId && output.videoCompositionId !== 'default') {
          console.info('Loading video composition for output', {
            videoCompositionId: output.videoCompositionId,
          });

          const videoComposition = await loadVideoComposition({ videoCompositionId: output.videoCompositionId }).catch(
            () => null as never
          );

          videoCompositions[output.videoCompositionId] = videoComposition;
        }

        // If we STILL don't have the video composition, my guy has been deleting files.
        if (output.videoCompositionId && !videoCompositions[output.videoCompositionId]) {
          console.info('Video composition not found during destination registration. Setting to default.', {
            videoCompositionId: output.videoCompositionId,
          });

          output.videoCompositionId = 'default';

          const { displayName, provider } = this.destinationMetadata;

          await navigateInModal({
            title: 'Video Composition Not Found',
            width: 600,
            height: 400,
            to: '/exception/notify',
            search: {
              message: encodeURIComponent(
                [
                  `The canvas which was used for ${displayName} (${platformPrettyName[provider]}) could not be found. The output will be reset to the default canvas.`,
                  'Please double check the settings of this output before going live.',
                  'If you are not sure what this means, please contact support.',
                ].join('\n')
              ),
              title: encodeURIComponent('Video Composition Not Found'),
            },
          });

          await this.updateDestination(
            {
              outputs: this.destinationMetadata.outputs.map((o) => {
                if (o.id === output.id) {
                  o.videoCompositionId = 'default';
                }
                return o;
              }),
            },
            {
              notifyOBS: false,
            },
            lockOptions,
          );
        }

        const updatePayload = {
          ...output,
          videoCompositionId: output.videoCompositionId ? videoCompositions[output.videoCompositionId]?.id : 'default',
          audioCompositionId: output.audioCompositionId ? audioCompositions[output.audioCompositionId]?.id : 'default',
          auxiliaryData: {
            ...output.auxiliaryData,
            destinationId: this.destinationMetadata.id,
            isPrimaryOutput: output.auxiliaryData?.isPrimaryOutput ?? false,
          },
        };

        if (output.auxiliaryData?.isPrimaryOutput && output.isEnabled) {
          updatePayload.isEnabled = false;
        }

        const isSuccess = await promisefy(window.host.addStreamingOutput)(updatePayload);

        // console.trace('addStreamingOutput: ', updatePayload);

        if (!isSuccess) {
          const { error, eventId } = createAndCaptureError(new FatalError('Failed to register streaming output, likely because the video composition is not present'));

          console.warn('Failed to register streaming output, likely because the video composition is not present', {
            output: JSON.stringify(output),
            videoCompositions: JSON.stringify(Object.values(videoCompositions).map((v) => [v.id, v.name])),
            traceEventId: eventId,
          });

          throw error;
        }

        if (stoppedOutputIds.includes(output.id) && !output.auxiliaryData?.isPrimaryOutput) {
          await promisefy(window.host.enableStreamingOutputsByIds)([output.id]);
        }
      }
    });
  }

  /**
   * @param {object} props - An object containing configuration options.
   * @param {'default' | string} props.videoCompositionId - The video composition ID to deregister the destination for. If provided, only outputs for that video composition will be deregistered.
   */
  public async deregisterDestination({ videoCompositionId }: { videoCompositionId?: VideoCompositionId } = {}, lockOptions?: LockOptions) {
    return await locked('deregisterDestination', async () => {
      const outputsBefore = await promisefy(window.host.getAllStreamingOutputs)();

      const outputIds = Object.entries(outputsBefore)
        .filter(([id, output]) => {
          if (videoCompositionId && output.videoCompositionId !== videoCompositionId) {
            return false;
          }
          return id.startsWith(this.destinationOutputIdTemplate);
        })
        .map(([id]) => id);

      const idsToStop = outputIds.filter((id) => outputsBefore[id]?.isActive);

      if (idsToStop.length) {
        const isStopped = await promisefy(window.host.disableStreamingOutputsByIds)(idsToStop.filter(i => i !== 'default'));

        if (!isStopped) {
          console.warn('Failed to stop streaming outputs', idsToStop);
        }
      }

      const isSuccess = await promisefy(window.host.removeStreamingOutputsByIds)(outputIds);

      if (!isSuccess) {
        throw new Error('Failed to disable destination');
      }

      return {
        isSuccess,
        stoppedOutputIds: idsToStop,
        deregisteredOutputIds: outputIds,
      };
    }, lockOptions);
  }

  public async disableDestination(lockOptions?: LockOptions) {
    await locked('disableDestination', async () => {
      await promisefy(window.host.disableStreamingOutputsByIds)(
        this.destinationMetadata.outputs.map((output) => output.id).filter(i => i !== 'default')
      );

      await this.updateDestination(
        {
          outputs: this.destinationMetadata.outputs.map((output) => {
            output.isEnabled = false;
            return output;
          }),
        },
        { notifyOBS: false },
        { lock: false },
      );
    }, lockOptions);
  }

  public async disableOutputsByIds({ outputIds }: { outputIds: string[] }, lockOptions?: LockOptions) {
    await locked('disableOutputsByIds', async () => {
      const updatedList = this.destinationMetadata.outputs.map((output) => {
        if (outputIds.includes(output.id)) {
          output.isEnabled = false;
        }
        return output;
      });

      await this.updateDestination({ outputs: updatedList }, { notifyOBS: false }, { lock: false });

      await promisefy(window.host.disableStreamingOutputsByIds)(outputIds.filter(i => i !== 'default'));
    }, lockOptions);
  }
}

type DefaultSetupStreamRoomParams = {
  title: string;
  categoryId: string;
  outputId: string;
  destinationMetadata: Destination;
};

type StreamRoomParamsPerProvider = {
  x: DefaultSetupStreamRoomParams & {
    xChatAccessOption: XOutputAuxiliaryData['chatAccessOption'];
    xSourceId: string;
  };
  youtube: DefaultSetupStreamRoomParams & {
    privacyStatus: string;
    liveStreamId: string;
    broadcastId: string;
  };
};

export interface DestinationProviderApiClient<
  P extends AllowedProvider,
  SetupStreamRoomParams extends P extends keyof StreamRoomParamsPerProvider // Check if the provider is in the StreamRoomParamsPerProvider map
    ? StreamRoomParamsPerProvider[P] // If it is, use the type from the map
    : DefaultSetupStreamRoomParams = P extends keyof StreamRoomParamsPerProvider
    ? StreamRoomParamsPerProvider[P]
    : DefaultSetupStreamRoomParams, // But also default the param to make it optional
> {
  setupStreamRoom(params: SetupStreamRoomParams): Promise<void>;

  updateStreamRoom(params: {
    outputId: string;
    title?: string;
    categoryId?: string;
    privacyStatus?: string;
  }): Promise<void>;

  getCategories(params: { query: string }): Promise<Map<string, string>>;
  getCategoryById(params: { categoryId: string }): Promise<{ id: string; displayName: string } | null>;

  endBroadcast(
    params: P extends 'youtube' | 'x' | 'tiktok'
      ? {
          broadcastId: string;
        }
      : never
  ): Promise<void>;
}

export function createOutputDestinationStreamSettingsProvider({
  authContextProvider,
  serviceEnvironment,
  destinationMetadata,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
  destinationMetadata: Destination;
}): DestinationProviderApiClient<typeof destinationMetadata.provider> {
  if (!destinationMetadata) {
    throw new Error('Destination metadata is not set');
  }

  switch (destinationMetadata.provider) {
    case 'twitch': {
      return new TwitchApiClient({
        authContextProvider,
        serviceEnvironment,
        destinationMetadata,
      });
    }
    case 'youtube': {
      return new YoutubeApiClient({
        authContextProvider,
        serviceEnvironment,
        destinationMetadata: destinationMetadata as Destination<'youtube'>,
      });
    }
    case 'x': {
      return new XApiClient({
        authContextProvider,
        serviceEnvironment,
        destinationMetadata: destinationMetadata as Destination<'x'>,
      });
    }
    case 'tiktok': {
      return new TiktokApiClient({
        authContextProvider,
        serviceEnvironment,
        tiktokAuthMetadata: destinationMetadata,
      });
    }
    case 'trovo': {
      return new TrovoApiClient({
        authContextProvider,
        serviceEnvironment,
        destinationMetadata,
      });
    }
    case 'custom': {
      return {
        setupStreamRoom: async () => void 0,
        getCategories: async () => {
          return new Map<string, string>();
        },
        updateStreamRoom: async () => void 0,
        endBroadcast: async () => void 0,
        getCategoryById: async () => null,
      };
    }
    case 'kick': {
      return new KickApiClient({
        authContextProvider,
        serviceEnvironment,
        destinationMetadata,
      });
    }
    default:
      throw new Error('Unsupported provider');
  }
}
